import { Drawer } from '@mantine/core';
import clsx from 'clsx';
import { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { darkModeRoot } from 'src/app/App.css';
import { SeatingInfo } from 'src/components/common/SeatingInfo';
import { MobileHeaderBar } from 'src/components/mobile/MobileHeader';
import { vars } from 'src/core/themes';
import { globalMobileWrapper } from 'src/core/themes/themeContract.css';
import { Stack } from 'src/core/ui';
import { LaunchWasteUnsoldTickets } from 'src/modals/ListingDetails/actions/LaunchWasteUnsoldTickets';
import { ChevronRightIcon } from 'src/svgs/Viagogo';
import { getListingActionButtons } from 'src/utils/inventoryUtils';
import { getSaleDetailsRelativeUrl } from 'src/utils/saleUtils';
import { Event, Listing, ListingActionType } from 'src/WebApiController';

import * as drawerStyles from './MobileListingDrawer.css';
import { MobileListingDrawerZIndex } from './MobileListingDrawerContext.constants';

export const PredeliveryActionTypes = [
  ListingActionType.PredeliverBarcodes,
  ListingActionType.PredeliverETickets,
  ListingActionType.PredeliverExternalIds,
  ListingActionType.PredeliverQRCode,
  ListingActionType.PredeliverTicketUrls,
  ListingActionType.RedeliverBarcodes,
  ListingActionType.RedeliverETickets,
  ListingActionType.RedeliverExternalIds,
  ListingActionType.RedeliverQRCode,
  ListingActionType.RedeliverTicketUrls,
];

type ListingActionProps = {
  key: ListingActionType;
  action: JSX.Element;
};

export const MobileListingActionsDrawer = ({
  listing,
  event,
  show,
  onClose,
}: {
  listing: Listing;
  event: Event;
  show: boolean;
  onClose: () => void;
}) => {
  const navigate = useNavigate();

  const navigateToWastedSale = useCallback(
    (saleId: number) => {
      navigate(getSaleDetailsRelativeUrl(saleId));
    },
    [navigate]
  );
  const listingActions = useMemo((): ListingActionProps[] => {
    const actions = listing.actions
      .filter(
        (actionType): actionType is ListingActionType =>
          actionType !== ListingActionType.FullySoldNoAction &&
          actionType !== ListingActionType.Waste &&
          !PredeliveryActionTypes.includes(actionType)
      )
      .map((actionType) => {
        const actions = getListingActionButtons(
          false,
          listing,
          [actionType],
          event,
          'textPlain',
          false /* icon only mode */,
          undefined,
          false,
          undefined /* lockListingGroupFromEdits */,
          undefined /* onSetListingActiveInGroup */,
          'fullscreen'
        );

        if (actions?.length) {
          return { key: actionType, action: actions?.[0] };
        }
        return undefined;
      })
      .filter(
        (
          action: ListingActionProps | undefined
        ): action is ListingActionProps => action != null
      );

    if (listing.actions.includes(ListingActionType.Waste)) {
      actions.push({
        key: ListingActionType.Waste,
        action: (
          <LaunchWasteUnsoldTickets
            modalSize="fullscreen"
            onWastedSuccess={navigateToWastedSale}
          />
        ),
      });
    }

    return actions;
  }, [event, listing, navigateToWastedSale]);

  return (
    <Drawer.Root
      opened={show}
      onClose={onClose}
      size="100%"
      position="right"
      className={clsx(darkModeRoot, globalMobileWrapper)}
      styles={{
        body: {
          height: '100%',
          backgroundColor: vars.color.backgroundSecondary,
        },
      }}
      zIndex={MobileListingDrawerZIndex.ListingActions}
    >
      <Drawer.Content className={drawerStyles.drawerContent}>
        <Drawer.Header className={drawerStyles.drawerHeader}>
          <MobileHeaderBar
            onBack={onClose}
            title={
              <SeatingInfo
                section={listing.seating.section ?? ''}
                row={listing.seating.row}
                seatFr={listing.seating.seatFr}
                seatTo={listing.seating.seatTo}
                multiLineDisplay
                labelClassName={drawerStyles.seatingLabel}
              />
            }
          />
        </Drawer.Header>
        <Drawer.Body className={drawerStyles.drawerBody}>
          <Stack
            width="full"
            direction="column"
            gap="s"
            alignItems="center"
            className={drawerStyles.drawerInnerContent}
          >
            {listingActions.map(({ key, action }, index) => (
              <MobileListingAction index={index} key={key} action={action} />
            ))}
          </Stack>
        </Drawer.Body>
      </Drawer.Content>
    </Drawer.Root>
  );
};

/**
 * Wrapper component to forward the click event from the container to the actual action button
 */
const MobileListingAction = ({
  index,
  key,
  action,
}: {
  index: number;
  key: ListingActionType;
  action: JSX.Element;
}) => {
  const actionRef = useRef<HTMLDivElement>(null);
  const handleContainerClick = () => {
    // Find and click the first button/clickable element within the action
    const clickableElement = actionRef.current?.querySelector(
      'button, [role="button"], a'
    );
    if (clickableElement instanceof HTMLElement) {
      clickableElement.click();
    }
  };

  return (
    <Stack
      role="button"
      tabIndex={index}
      key={key}
      width="full"
      alignItems="center"
      justifyContent="spaceBetween"
      className={drawerStyles.listingActionContainer}
      onClick={handleContainerClick}
      style={{ cursor: 'pointer' }}
    >
      <div ref={actionRef}>{action}</div>
      <ChevronRightIcon size={vars.iconSize.s} />
    </Stack>
  );
};
